import { Button } from 'blaze-ui/Button'
import {
  InformalFeedbackRequest,
  InformalFeedbackReview,
  InformalFeedbackReviewScore,
} from 'api/clients/InformalFeedbackRequestsClient'
import { useSubmitReview } from 'hooks/queries/informalFeedbackRequests/Reviews/useSubmitReview'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import dayjs from 'dayjs'
import { useUpdateInformalFeedbackRequest } from 'hooks/queries/informalFeedbackRequests/useUpdateInformalFeedbackRequest'
import { reportError } from 'utils/error'
import useResponsiveFunction from 'hooks/useResponsiveFunction'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export interface Props {
  review: InformalFeedbackReview
  request: InformalFeedbackRequest
  isDisabled: boolean
  message: string
  score: InformalFeedbackReviewScore
  dueDate?: string
}

export const SubmitReviewButton = observer(({ review, request, isDisabled, score, message, dueDate }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { rightRailPanelStore, docStore, drawersStore } = useRootStore()
  const { mutate: updateRequest, isLoading: isUpdating } = useUpdateInformalFeedbackRequest(request.id)
  const { mutate: submitReview, isLoading: isSubmitting } = useSubmitReview({
    id: review.id,
    requestId: request.id,
    almanacFileId: request.almanac_file.id,
  })

  const openReviewSubmissionPanel = useResponsiveFunction({
    mobileFunction: () => drawersStore.closeAllDrawers(),
    defaultFunction: () => {
      docStore?.rightRailStore.setActiveTab('AIAssistant')
      rightRailPanelStore.hide()
    },
  })

  const handlePress = () => {
    if (score === InformalFeedbackReviewScore.NeedsMajorChanges && !dayjs(dueDate).isSame(dayjs(request.ends_on))) {
      return updateRequest(
        {
          ends_on: dueDate,
        },
        {
          onSuccess: submit,
          onError: (error) => {
            reportError(error)
            showErrorMessageToast('There was an error updating the request')
          },
        },
      )
    }

    submit()
  }

  const submit = () =>
    submitReview(
      { message, score },
      {
        onSuccess: openReviewSubmissionPanel,
        onError: (error) => {
          reportError(error)
          showErrorMessageToast('There was an error submitting the review')
        },
      },
    )

  return (
    <Button
      size={isMobile ? 'xl' : 'lg'}
      fullWidth
      isDisabled={isDisabled || isSubmitting || isUpdating}
      onPress={handlePress}
    >
      Submit Review
    </Button>
  )
})
